import { MenuItem, TextFieldProps } from '@mui/material';
import isNil from 'lodash/isNil';
import { FieldPath, FieldValues, useController, useFormContext } from 'react-hook-form';
import { PInputState } from '@/components/atoms/inputState';
import { PSelect } from '@/components/atoms/Select/PSelect';
import { applyWarningSx } from '@/components/hook-form/utils';
import WarningHelperText from '@/components/hook-form/WarningHelperText';

export type RHFSelectProps<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  children: any;
  state?: PInputState;
  /**
   * Beware native is a workaround for un-refactorable old code. Do not use native selects as the style is weird and
   * behavior inconsistent.
   */
  native?: boolean;
  onValueChange?: (value: any) => void;
  /**
   * If true an empty option will be included in the select with an empty string as value. (MUI requirement for a
   * select without a value) This option should only be set if a nullable value is a valid user input on the form. It
   * does not make sense to provide a nullable input on a required form field. Make sure to handle this null value both
   * in your submit function and form schema validation properly via a check for an empty string with `.when()` Yup
   * modifier.
   */
  includeEmptyOption?: boolean;
  warningText?: string;
  /**
   * A problem the form itself knows about rather than one the resolver found — a field the caller
   * cannot proceed without. Renders like a validation error and outranks `warningText`.
   */
  errorText?: string;
} & Omit<TextFieldProps, 'disabled' | 'select'>;

/**
 * Minimal@4 component.
 */

export default function RHFSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  children,
  state,
  native = false,
  onValueChange,
  helperText,
  warningText,
  errorText,
  includeEmptyOption = false,
  SelectProps,
  sx,
  ...other
}: RHFSelectProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();
  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController({ name, control });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    field.onChange(event.target.value);
    if (onValueChange) {
      onValueChange(event.target.value);
    }
  };

  const hasError = !isNil(error) || !isNil(errorText);
  // A warning tint under an error would fight the error's own styling for the same border.
  const effectiveSx = applyWarningSx(hasError ? undefined : warningText, sx);

  const resolvedHelperText = !isNil(error) ? (
    error.message
  ) : !isNil(errorText) ? (
    errorText
  ) : !isNil(warningText) ? (
    <WarningHelperText text={warningText} />
  ) : (
    helperText
  );

  return (
    <PSelect
      {...field}
      fullWidth
      error={hasError}
      helperText={resolvedHelperText}
      state={state}
      inputRef={ref}
      sx={effectiveSx}
      onChange={handleChange}
      // Beware native is a workaround for un-refactorable old code. Do not use native selects as the style is weird and behavior inconsistent.
      SelectProps={{
        native,
        ...SelectProps,
      }}
      {...other}
    >
      {includeEmptyOption ? <MenuItem value="" style={{ height: 36 }}></MenuItem> : null}
      {children}
    </PSelect>
  );
}
